// Static, display-only data + tiny pure helpers.
// User/session/abstract/admin state now lives on the backend — see /lib/api.js.

/* ---------------------------- Packages / GST ---------------------------- */
export const GST_RATE = 0.18;

export const PACKAGES = [
  {
    id: "delegate-api",
    label: "Delegate — API Member",
    description:
      "For registered members of the Association of Physicians of India.",
    regularBase: 3000,
    lateBase: 4000,
    spotBase: 5000,
    color: "navy",
  },
  {
    id: "delegate-nonapi",
    label: "Delegate — Non-API",
    description: "For physicians who are not registered API members.",
    regularBase: 4000,
    lateBase: 5000,
    spotBase: 6000,
    color: "green",
  },
  {
    id: "pg",
    label: "Post Graduate",
    description: "For students currently pursuing post-graduate medicine.",
    regularBase: 1500,
    lateBase: 2500,
    spotBase: 3500,
    color: "gold",
  },
  // "accompanying" is intentionally NOT part of the public package menu.
  // It is offered as a delegate-only add-on from the dashboard — see
  // AccompanyingPersons.jsx and the backend /accompanying endpoints.
];

// A lookup map that still resolves legacy accompanying registrations from
// the old flow (used by Dashboard for label rendering).
export const LEGACY_PACKAGE_LABELS = {
  accompanying: "Accompanying Person",
};

export const computeTotal = (base) =>
  Math.round((base + base * GST_RATE) * 100) / 100;

/* ------------------------- Pricing tiers by date ------------------------- */
// Server is authoritative; these mirror backend current_slot() for display.
export const EARLY_BIRD_END = new Date("2026-11-15T23:59:59+05:30");
export const SPOT_START = null;
export const SLOT_LABELS = { regular: "Early-bird", late: "Late", spot: "Spot" };
export const ACCOMPANYING_BASES = { regular: 1500, late: 2500, spot: 3500 };

export const currentSlot = (now = new Date()) => {
  if (SPOT_START && now >= SPOT_START) return "spot";
  return now <= EARLY_BIRD_END ? "regular" : "late";
};

export const baseFor = (pkg, slot) => pkg?.[`${slot}Base`] ?? pkg?.regularBase;

// ---------------------------- Bank / Secretariat --------------------------
// Official APICON 2026 payment + contact details.
export const BANK_DETAILS = {
  accountName:
    "Association of Physician of India Assam Chapter Tinsukia Branch",
  accountNumber: "00000045247751258",
  ifsc: "SBIN0065266",
  bankName: "State Bank of India",
  branch: "Borguri Branch, Tinsukia",
};

export const SECRETARIAT = {
  venue: "Tinsukia Medical College & Hospital",
  city: "Tinsukia, Assam · India",
  dates: "18 – 20 December, 2026",
  email: "assamapicon2026tinsukia@gmail.com",
  phones: ["+91 94350 30504", "+91 94351 34082", "+91 94351 71036"],
};

export const COMPLIANCE = {
  gst: "18AAWAA4169M1ZW",
  pan: "AAWAA4169M",
};

// Legacy aliases retained so existing imports don't break; UPI QR is now
// generated dynamically on the payment page with the exact amount prefilled.
export const UPI_VPA = "sbibhim.022211900657971-ym648011@sbipay";
export const UPI_PAYEE = "ASSOCIATION OF PHYSICIANS";
export const UPI_MC = "8099";

// Build a UPI intent URL that opens any Indian UPI app with the amount
// pre-filled and non-editable on the payer side. `am` is amount in INR
// (float allowed but we truncate to 2 decimals). `tn` is the merchant note.
export const buildUpiUrl = ({ vpa, name, amount, note, txnRef }) => {
  const params = new URLSearchParams({
    pa: vpa,
    pn: name,
    am: Number(amount).toFixed(2),
    cu: "INR",
    tn: note || "APICON 2026 registration",
    mc: UPI_MC,
  });
  if (txnRef) params.set("tr", txnRef);
  return `upi://pay?${params.toString()}`;
};

/* ---------------------------- Committee data ---------------------------- */
export const COMMITTEE = [
  { name: "Dr. Arvind Choudhury", role: "Chief Patron",
    bio: "Vice Chancellor, Assam Medical University · Four decades in internal medicine and public health.",
    img: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Shanti Rajkhowa", role: "Patron",
    bio: "Former President, API North-East · Renowned endocrinologist and academic.",
    img: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Aparna Bora", role: "Organising Chairperson",
    bio: "HOD Internal Medicine, Tinsukia Medical College · Leads the conference operations end-to-end.",
    img: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Rajiv Saikia", role: "Scientific Committee Chair",
    bio: "Cardiologist, GMCH Guwahati · Curates the plenary and symposia programme.",
    img: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Meera Deka", role: "Organising Secretary",
    bio: "Nephrologist · Handles registration flow, delegate communications and secretariat.",
    img: "https://images.unsplash.com/photo-1638202993928-7267aad84c31?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Anirban Gogoi", role: "Treasurer",
    bio: "Pulmonologist · Oversees finance, sponsorships and vendor coordination.",
    img: "https://images.unsplash.com/photo-1537368910025-700350fe46c7?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Kavita Baruah", role: "Vice Chairperson",
    bio: "Rheumatologist · Supports scientific and logistics arms of the committee.",
    img: "https://images.unsplash.com/photo-1651008376811-b90baee60c1f?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Pranab Neog", role: "Joint Secretary",
    bio: "Gastroenterologist · Coordinates faculty travel and workshop logistics.",
    img: "https://images.unsplash.com/photo-1582750433449-648ed127bb54?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Ishani Handique", role: "Scientific Co-Chair",
    bio: "Diabetologist · Reviews abstracts, chairs the poster committee.",
    img: "https://images.unsplash.com/photo-1607990281513-2c110a25bd8c?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Bijoy Phukan", role: "Sponsorship Chair",
    bio: "Hepatologist · Leads industry partnerships and exhibitor relations.",
    img: "https://images.unsplash.com/photo-1622902046580-2b47f47f5471?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Rituparna Das", role: "Cultural Chair",
    bio: "Physician & Bihu enthusiast · Curates the cultural evenings and heritage trail.",
    img: "https://images.unsplash.com/photo-1571772996211-2f02c9727629?auto=format&fit=crop&w=600&q=80" },
  { name: "Dr. Samir Hazarika", role: "Registration Chair",
    bio: "Internist · Handles delegate onboarding, IDs and the welcome kits.",
    img: "https://images.unsplash.com/photo-1584982751601-97dcc096659c?auto=format&fit=crop&w=600&q=80" },
];

/* ---------------------------- Gallery data ---------------------------- */
export const GALLERY = [
  { id: "g01", src: "https://images.unsplash.com/photo-1524492412937-b28074a5d7da?auto=format&fit=crop&w=1200&q=80",
    title: "Tea gardens at first light", category: "Tinsukia",
    caption: "Assam's endless carpets of green — a five-minute drive from the venue." },
  { id: "g02", src: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=1200&q=80",
    title: "Plenary hall setup", category: "Venue",
    caption: "The main auditorium at Tinsukia Medical College, prepped for a keynote." },
  { id: "g03", src: "https://images.unsplash.com/photo-1587854692152-cbe660dbde88?auto=format&fit=crop&w=1200&q=80",
    title: "Delegate onboarding", category: "Sessions",
    caption: "Volunteers guiding delegates through registration and welcome kits." },
  { id: "g04", src: "https://images.unsplash.com/photo-1497366216548-37526070297c?auto=format&fit=crop&w=1200&q=80",
    title: "Symposium in progress", category: "Sessions",
    caption: "A packed room — panel on precision internal medicine." },
  { id: "g05", src: "https://images.unsplash.com/photo-1544006659-f0b21884ce1d?auto=format&fit=crop&w=1200&q=80",
    title: "Bihu evening", category: "Cultural",
    caption: "The cultural evening — Bihu dancers welcoming faculty." },
  { id: "g06", src: "https://images.unsplash.com/photo-1533900298318-6b8da08a523e?auto=format&fit=crop&w=1200&q=80",
    title: "Assam tea rituals", category: "Cultural",
    caption: "A first-flush tasting session with local growers." },
  { id: "g07", src: "https://images.unsplash.com/photo-1519074002996-a69e7ac46a42?auto=format&fit=crop&w=1200&q=80",
    title: "Hands-on workshop", category: "Workshops",
    caption: "Point-of-care ultrasound workshop, morning session." },
  { id: "g08", src: "https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1200&q=80",
    title: "Faculty dinner", category: "Cultural",
    caption: "Faculty dinner at a heritage bungalow, evening two." },
  { id: "g09", src: "https://images.unsplash.com/photo-1470137237906-d8a4f71e1966?auto=format&fit=crop&w=1200&q=80",
    title: "Dibru–Saikhowa reserve", category: "Tinsukia",
    caption: "An optional dawn trail to the wildlife reserve, before day three." },
  { id: "g10", src: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80",
    title: "Poster halls", category: "Sessions",
    caption: "Post-graduate poster displays lining the west foyer." },
  { id: "g11", src: "https://images.unsplash.com/photo-1502920917128-1aa500764cbd?auto=format&fit=crop&w=1200&q=80",
    title: "Namdapha vistas", category: "Tinsukia",
    caption: "A short flight from Tinsukia — worth the detour on day four." },
  { id: "g12", src: "https://images.unsplash.com/photo-1517048676732-d65bc937f952?auto=format&fit=crop&w=1200&q=80",
    title: "Networking break", category: "Sessions",
    caption: "Tea break — where the best conversations happen." },
];

export const GALLERY_CATEGORIES = [
  "All",
  ...Array.from(new Set(GALLERY.map((g) => g.category))),
];

/* ---------------------------- Abstracts config ---------------------------- */
export const ABSTRACT_CATEGORIES = [
  { id: "case-report", label: "Case Report" },
  { id: "original-research", label: "Original Research" },
  { id: "systematic-review", label: "Systematic Review" },
  { id: "poster", label: "Poster" },
];

export const ABSTRACT_STATUSES = {
  draft: { label: "Draft", tone: "bg-gray-100 text-gray-700" },
  submitted: { label: "Submitted", tone: "bg-amber-50 text-amber-800" },
  "under-review": { label: "Under review", tone: "bg-blue-50 text-blue-800" },
  accepted: { label: "Accepted", tone: "bg-emerald-50 text-emerald-700" },
  rejected: { label: "Rejected", tone: "bg-red-50 text-red-700" },
};

export const wordCount = (text) =>
  (text || "").trim().split(/\s+/).filter(Boolean).length;

/* ---------------------------- UPI helpers ---------------------------- */
// (buildUpiUrl is defined above alongside the merchant VPA constants.)

export const generateTxnRef = (userId) => {
  const short = (userId || "guest").replace(/[^a-zA-Z0-9]/g, "").slice(-6).toUpperCase();
  const ts = Date.now().toString(36).toUpperCase();
  return `APICON-${short}-${ts}`;
};
